import React, { useState } from "react";
import {
  useCreateHttpEndpoint,
  useDeleteHttpEndpoint,
  useHttpConfigs,
  useHttpEndpoints,
  useUpdateHttpEndpoint,
} from "../../hooks/useHttp";
import LoadingSpinner from "../../components/ui/LoadingSpinner";
import ErrorMessage from "../../components/ui/ErrorMessage";
import { Input, Textarea } from "../../components/ui/Input";
import { useNavigate } from "react-router";

interface EndpointFormData {
  config_id: number | "";
  name: string;
  path: string;
  method: string;
  params?: string;
  headers?: string;
  body_template?: string;
  response_schema?: string;
  description?: string;
}

export default function HttpEndpointsPage() {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formData, setFormData] = useState<EndpointFormData>({
    config_id: "",
    name: "",
    path: "",
    method: "GET",
    params: "{}",
    headers: "{}",
    body_template: "",
    response_schema: "{}",
    description: "",
  });
  const navigate = useNavigate();

  const { data, isLoading, isError, refetch } = useHttpEndpoints({
    page,
    page_size: 12,
    search: search || undefined,
  });
  const configsQuery = useHttpConfigs({ page: 1, page_size: 100 });
  const createMutation = useCreateHttpEndpoint();
  const updateMutation = useUpdateHttpEndpoint();
  const deleteMutation = useDeleteHttpEndpoint();

  const items = data?.endpoints || [];
  const totalPages = data?.total_pages || 1;
  const configs = configsQuery.data?.configs || [];

  const openCreate = () => {
    setEditingId(null);
    setFormData({
      config_id: "",
      name: "",
      path: "",
      method: "GET",
      params: "{}",
      headers: "{}",
      body_template: "",
      response_schema: "{}",
      description: "",
    });
    setShowForm(true);
  };

  const openEdit = (row: any) => {
    navigate(`/http-endpoints/${row.id}/edit`);
  };

  const submitForm = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.config_id) return;
    const payload = {
      config_id: Number(formData.config_id),
      name: formData.name,
      path: formData.path,
      method: formData.method || "GET",
      params: safeJson(formData.params),
      headers: safeJson(formData.headers),
      body_template: formData.body_template || undefined,
      response_schema: safeJson(formData.response_schema),
      description: formData.description || undefined,
    };
    if (editingId) {
      await updateMutation.mutateAsync({ id: editingId, data: payload });
    } else {
      await createMutation.mutateAsync(payload);
    }
    setShowForm(false);
    refetch();
  };

  const onDelete = async (id: number) => {
    await deleteMutation.mutateAsync(id);
    refetch();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">HTTP Endpoints</h1>
          <p className="text-gray-600 mt-1">
            Manage endpoint configurations for HTTP requests
          </p>
        </div>
        <button
          onClick={openCreate}
          className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
        >
          Create Endpoint
        </button>
      </div>

      <div className="bg-white p-4 rounded-lg border border-gray-200 flex items-center gap-3">
        <Input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name or description"
          variant="default"
          className="flex-1"
          showLabel={false}
        />
        <button
          onClick={() => setPage(1)}
          className="px-3 py-2 bg-gray-100 rounded border"
        >
          Apply
        </button>
        <button
          onClick={() => {
            setSearch("");
            setPage(1);
          }}
          className="px-3 py-2 bg-gray-100 rounded border"
        >
          Clear
        </button>
      </div>

      {isLoading && (
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner size="lg" />
        </div>
      )}
      {isError && (
        <ErrorMessage
          message="Failed to load endpoints"
          onRetry={() => refetch()}
        />
      )}

      {!isLoading && !isError && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((row: any) => (
            <div key={row.id} className="bg-white border rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-lg font-semibold">{row.name}</h3>
                <span className="text-xs text-gray-500">#{row.id}</span>
              </div>
              <div className="text-sm text-gray-600 mb-1">
                Config: <span className="font-mono">{row.config_id}</span>
              </div>
              <div className="text-sm text-gray-600 mb-1">
                {row.method}{" "}
                <span className="font-mono break-all">{row.path}</span>
              </div>
              {row.description && (
                <div className="text-sm text-gray-700 mb-3">
                  {row.description}
                </div>
              )}
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => openEdit(row)}
                  className="px-3 py-1 text-sm bg-white border rounded"
                >
                  Edit
                </button>
                <button
                  onClick={() => onDelete(row.id)}
                  className="px-3 py-1 text-sm bg-red-500 text-white rounded"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {!isLoading && !isError && (data?.total_pages || 0) > 1 && (
        <div className="flex items-center justify-center space-x-2">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="px-3 py-2 bg-white border rounded disabled:opacity-50"
          >
            Previous
          </button>
          <span className="text-sm">
            Page {page} of {totalPages}
          </span>
          <button
            onClick={() => setPage((p) => p + 1)}
            disabled={page === totalPages}
            className="px-3 py-2 bg-white border rounded disabled:opacity-50"
          >
            Next
          </button>
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-lg w-full">
            <div className="p-6">
              <h2 className="text-xl font-semibold mb-4">
                {editingId ? "Edit Endpoint" : "Create Endpoint"}
              </h2>
              <form onSubmit={submitForm} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Config
                  </label>
                  <select
                    value={formData.config_id}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        config_id: e.target.value ? Number(e.target.value) : "",
                      })
                    }
                    className="w-full px-3 py-2 border rounded"
                    required
                  >
                    <option value="">Select config</option>
                    {configs.map((c: any) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Name
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    className="w-full px-3 py-2 border rounded"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Path
                  </label>
                  <input
                    type="text"
                    value={formData.path}
                    onChange={(e) =>
                      setFormData({ ...formData, path: e.target.value })
                    }
                    className="w-full px-3 py-2 border rounded"
                    placeholder="/users/{id}"
                    required
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Method
                    </label>
                    <select
                      value={formData.method}
                      onChange={(e) =>
                        setFormData({ ...formData, method: e.target.value })
                      }
                      className="w-full px-3 py-2 border rounded"
                    >
                      <option value="GET">GET</option>
                      <option value="POST">POST</option>
                      <option value="PUT">PUT</option>
                      <option value="PATCH">PATCH</option>
                      <option value="DELETE">DELETE</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description
                    </label>
                    <input
                      type="text"
                      value={formData.description || ""}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          description: e.target.value,
                        })
                      }
                      className="w-full px-3 py-2 border rounded"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Textarea
                      label="Params (JSON)"
                      value={formData.params}
                      onChange={(e) =>
                        setFormData({ ...formData, params: e.target.value })
                      }
                      variant="rounded-md"
                      className="font-mono"
                      rows={3}
                    />
                  </div>
                  <div>
                    <Textarea
                      label="Headers (JSON)"
                      value={formData.headers}
                      onChange={(e) =>
                        setFormData({ ...formData, headers: e.target.value })
                      }
                      variant="rounded-md"
                      className="font-mono"
                      rows={3}
                    />
                  </div>
                </div>
                <div>
                  <Textarea
                    label="Body Template"
                    value={formData.body_template}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        body_template: e.target.value,
                      })
                    }
                    variant="rounded-md"
                    className="font-mono"
                    rows={4}
                    placeholder="JSON or string template"
                  />
                </div>
                <div>
                  <Textarea
                    label="Response Schema (JSON)"
                    value={formData.response_schema}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        response_schema: e.target.value,
                      })
                    }
                    variant="rounded-md"
                    className="font-mono"
                    rows={4}
                  />
                </div>
                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => setShowForm(false)}
                    className="px-4 py-2 bg-gray-100 border rounded"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-500 text-white rounded"
                  >
                    {editingId ? "Save Changes" : "Create"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function safeJson(text?: string) {
  if (!text) return undefined;
  try {
    const obj = JSON.parse(text);
    return obj;
  } catch {
    return undefined;
  }
}
